import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/utils/api";
import PublicLayout from "@/components/layout/PublicLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Search, CheckCircle2, Truck, ArrowRight, AlertTriangle, RotateCcw,
  Package, Loader2, ChevronDown, ChevronUp,
} from "lucide-react";

const STATUS_STYLE = (s) => {
  const k = (s || "").toUpperCase();
  if (k === "DELIVERED" || k === "3PL_DELIVERED")
    return { bg: "bg-emerald-50 border-emerald-300 text-emerald-800", icon: CheckCircle2 };
  if (k === "OUT_FOR_DELIVERY" || k === "3PL_OUT_FOR_DELIVERY")
    return { bg: "bg-teal-50 border-teal-300 text-teal-800", icon: Truck };
  if (k === "IN_TRANSIT" || k === "3PL_IN_TRANSIT" || k === "FM_IN_TRANSIT")
    return { bg: "bg-blue-50 border-blue-300 text-blue-800", icon: ArrowRight };
  if (k === "NDR_RAISED" || k === "3PL_NDR" || k === "DELIVERY_ATTEMPTED")
    return { bg: "bg-amber-50 border-amber-300 text-amber-800", icon: AlertTriangle };
  if (k === "RTO_INITIATED" || k === "LOST")
    return { bg: "bg-red-50 border-red-300 text-red-800", icon: RotateCcw };
  return { bg: "bg-slate-100 border-slate-300 text-slate-700", icon: Package };
};

const fmtType = (t) =>
  (t || "")
    .replace(/^3PL_/, "Courier ")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const fmtDate = (s) => {
  if (!s) return "—";
  try {
    return new Date(s).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return s;
  }
};

export function TrackingTimeline({ events, embedded = false }) {
  const [expanded, setExpanded] = useState(false);
  if (!events?.length) {
    return (
      <div className={`text-center py-8 text-slate-400 ${embedded ? "" : "border border-dashed border-slate-200 rounded-xl"}`}>
        <Package className="w-8 h-8 mx-auto mb-2 opacity-60" />
        <p className="text-sm">No tracking events yet</p>
      </div>
    );
  }
  // Latest first
  const sorted = [...events].sort(
    (a, b) => new Date(b.event_date || 0) - new Date(a.event_date || 0)
  );
  const show = expanded ? sorted : sorted.slice(0, 3);
  return (
    <div data-testid="tracking-timeline" className="relative">
      <div className="absolute left-3 top-2 bottom-2 w-px bg-slate-200" />
      <div className="space-y-4">
        {show.map((ev, i) => {
          const isLatest = i === 0;
          return (
            <div key={ev.event_id || i} className="relative pl-9">
              <div
                className={`absolute left-1 top-1.5 w-5 h-5 rounded-full border-2 ${
                  isLatest
                    ? "bg-slate-800 border-slate-800 animate-pulse"
                    : "bg-emerald-500 border-emerald-500"
                }`}
              />
              <div className={`${isLatest ? "" : ""}`}>
                <div className="font-semibold text-slate-800 text-sm">
                  {fmtType(ev.event_type)}
                </div>
                <div className="text-sm text-slate-600">{ev.display_message}</div>
                {ev.display_location && (
                  <div className="text-xs text-slate-500 mt-0.5">📍 {ev.display_location}</div>
                )}
                <div className="text-xs text-slate-400 mt-1">{fmtDate(ev.event_date)}</div>
              </div>
            </div>
          );
        })}
      </div>
      {sorted.length > 3 && (
        <button
          onClick={() => setExpanded((s) => !s)}
          className="mt-3 ml-9 text-xs text-blue-600 font-semibold flex items-center gap-1"
          data-testid="toggle-timeline-expand"
        >
          {expanded ? (
            <>
              <ChevronUp className="w-3.5 h-3.5" /> Show less
            </>
          ) : (
            <>
              <ChevronDown className="w-3.5 h-3.5" /> Show all {sorted.length} events
            </>
          )}
        </button>
      )}
    </div>
  );
}

export default function TrackShipment() {
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState(params.get("awb") || params.get("tid") || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const search = async (q) => {
    const term = (q || query || "").trim();
    if (!term) return;
    setLoading(true);
    setError("");
    setData(null);
    try {
      const r = await api.get(`/v2/tracking/${encodeURIComponent(term)}`);
      const d = r.data;
      if (!d.found && (!d.events || !d.events.length)) {
        // Try by LR — many shipments use LR identical to AWB so the first call covers both.
        // If not found, surface error.
        setError("No shipment found for this AWB/LR number");
      } else {
        setData(d);
      }
    } catch (e) {
      setError(e.response?.data?.detail || "Unable to fetch tracking. Try again.");
    } finally {
      setLoading(false);
    }
  };

  // Auto-search if param present
  useEffect(() => {
    if (query) search(query);
  }, []);

  const statusInfo = data?.current_status ? STATUS_STYLE(data.current_status) : null;
  const StatusIcon = statusInfo?.icon || Package;
  const latest = data?.events?.[data.events.length - 1];

  return (
    <PublicLayout>
      <div className="max-w-2xl mx-auto py-10 px-4" data-testid="public-track-page">
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-slate-800">Track Your Shipment</h1>
          <p className="text-sm text-slate-500 mt-1">
            Enter your AWB or LR number for real-time status
          </p>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const term = query.trim();
              if (term) setParams({ awb: term });
              search();
            }}
            className="flex gap-2"
          >
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value.trim())}
                placeholder="Enter Order ID, AWB or LR number (e.g. 02082601)"
                className="pl-8"
                data-testid="track-input"
              />
            </div>
            <Button
              type="submit"
              disabled={loading || !query}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              data-testid="track-search-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Track"}
            </Button>
          </form>
        </div>

        {error && (
          <div
            className="mt-5 bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700 flex items-start gap-2"
            data-testid="track-error"
          >
            <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5" />
            {error}
          </div>
        )}

        {data && (
          <div className="mt-5 space-y-5">
            <div className="bg-white border border-slate-200 rounded-xl p-4 grid grid-cols-2 gap-2 text-sm">
              <Info label="AWB" value={query} />
              <Info label="LR Number" value={data.lr_number} />
              <div>
                <div className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">Current Status</div>
                <div className="text-emerald-600 font-bold">{fmtType(data.current_status) || "—"}</div>
              </div>
            </div>
            <div
              className={`border rounded-xl p-5 flex items-start gap-3 ${statusInfo.bg}`}
              data-testid="status-banner"
            >
              <StatusIcon className="w-8 h-8 flex-shrink-0" />
              <div className="flex-1">
                <div className="text-lg font-bold">{fmtType(data.current_status)}</div>
                {latest?.display_location && (
                  <div className="text-sm opacity-90 mt-0.5">📍 {latest.display_location}</div>
                )}
                {latest?.event_date && (
                  <div className="text-xs opacity-75 mt-1">{fmtDate(latest.event_date)}</div>
                )}
              </div>
            </div>

            <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
              <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider mb-4">
                Journey
              </h3>
              <TrackingTimeline events={data.events} />
            </div>

            </div>
        )}
      </div>
    </PublicLayout>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase font-bold text-slate-500 tracking-wider">{label}</div>
      <div className="text-slate-800 font-medium">{value || "—"}</div>
    </div>
  );
}
